// Git (tela, artboard 22 e 26): l'archivio dei server git che i progetti possono usare, una
// pagina sua fra le Risorse.
// Il token entra e non torna: in modifica il campo vuoto tiene quello salvato.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { GitRegistries, GitRegistry, GitRegistryInput, GitTest } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Field, TextField } from '../../components/Field';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { PageHeader } from '../../components/PageHeader';
import { Segmented } from '../../components/Segmented';
import { SkeletonRow } from '../../components/Skeleton';
import { useStore } from '../../store/StoreProvider';

const HOST: Record<string, string> = { github: 'https://github.com' };

function detail(e: unknown, fallback: string): string {
  return e instanceof ApiError && e.detail ? e.detail : fallback;
}

function status(r: GitRegistry): { text: string; tone: string } {
  if (!r.lastTest) return { text: 'Non provato', tone: 'neutral' };
  return r.lastTest.ok
    ? { text: 'Token valido', tone: 'ok' }
    : { text: 'Token rifiutato', tone: 'danger' };
}

function RegistryCard({
  r,
  label,
  onTest,
  onEdit,
}: {
  r: GitRegistry;
  label: string;
  onTest: () => Promise<void>;
  onEdit: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const s = status(r);
  const used = r.usedBy.length
    ? `usato da ${r.usedBy.length} ${r.usedBy.length === 1 ? 'progetto' : 'progetti'}`
    : 'nessun progetto';
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-xl)',
        background: 'var(--panel)',
        padding: '14px 16px',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          flex: 'none',
          borderRadius: 'var(--r-md)',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Icon name="git-fork" size={18} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ font: '600 14px/20px var(--font-ui)' }}>{r.name}</span>
          <span className="t-caption faint">{label}</span>
          <span
            style={{
              font: '600 12px/16px var(--font-ui)',
              color: `var(--${s.tone})`,
              background: `var(--${s.tone}-soft)`,
              padding: '2px 8px',
              borderRadius: 'var(--r-full)',
            }}
          >
            {s.text}
          </span>
        </div>
        <div className="t-mono-xs dim" style={{ overflowWrap: 'anywhere' }}>
          {r.url} · {r.username}
        </div>
        <div className="t-caption dim" style={{ marginTop: 2 }}>
          {r.lastTest ? r.lastTest.detail : 'Premi «Prova» per verificare il token'} · {used}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <Button
            size="sm"
            icon="refresh-cw"
            loading={busy}
            onClick={() => {
              setBusy(true);
              void onTest().finally(() => setBusy(false));
            }}
          >
            Prova
          </Button>
          <Button size="sm" onClick={onEdit}>
            Modifica
          </Button>
        </div>
      </div>
    </div>
  );
}

function RegistryModal({
  registry,
  kinds,
  onClose,
  onSaved,
  onRemove,
}: {
  registry: GitRegistry | null;
  kinds: GitRegistries['kinds'];
  onClose: () => void;
  onSaved: () => void;
  onRemove: (r: GitRegistry) => void;
}) {
  const [form, setForm] = useState<GitRegistryInput>(
    registry
      ? { ...registry, token: '' }
      : { name: '', kind: 'gitea', url: '', username: '', token: '' },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<GitTest | null>(registry?.lastTest ?? null);
  // dopo il primo salvataggio (anche solo per la prova) il registro esiste: si modifica quello
  const [savedId, setSavedId] = useState<string | null>(registry?.id ?? null);
  const set = (patch: Partial<GitRegistryInput>) => setForm((f) => ({ ...f, ...patch }));
  const ready =
    form.name.trim() && form.url.trim() && form.username.trim() && (savedId || form.token.trim());

  // la prova si fa sul registro salvato: prima si salva, poi si prova
  const save = async (andTest: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const saved = savedId
        ? await api.saveGitRegistry(savedId, form)
        : await api.addGitRegistry(form);
      setSavedId(saved.id);
      if (andTest) setTest(await api.testGitRegistry(saved.id));
      else onClose();
      onSaved();
    } catch (e) {
      setError(detail(e, 'Non riesco a salvare.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={registry ? `Modifica ${registry.name}` : 'Aggiungi git'}
      width={520}
      onClose={onClose}
      footer={
        <>
          {registry && (
            <Button icon="trash" onClick={() => onRemove(registry)} style={{ marginRight: 'auto' }}>
              Togli
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button icon="refresh-cw" disabled={!ready || busy} onClick={() => void save(true)}>
            Prova
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!ready}
            onClick={() => void save(false)}
          >
            Salva
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Nome">
          <TextField value={form.name} onChange={(v) => set({ name: v })} autoFocus={!registry} />
        </Field>
        <Field label="Tipo">
          <Segmented
            label="Tipo"
            value={form.kind}
            items={kinds.map((k) => ({ value: k.id, label: k.label }))}
            onChange={(v) => set({ kind: v, url: form.url || HOST[v] || '' })}
          />
        </Field>
        <Field label="Indirizzo del server">
          <TextField
            mono
            value={form.url}
            onChange={(v) => set({ url: v })}
            placeholder="https://git.example.org"
          />
        </Field>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
          }}
        >
          <Field label="Utente">
            <TextField mono value={form.username} onChange={(v) => set({ username: v })} />
          </Field>
          <Field
            label="Token"
            hint={
              registry
                ? 'Vuoto: resta quello salvato, se indirizzo e tipo non cambiano.'
                : undefined
            }
          >
            <TextField
              mono
              type="password"
              autoComplete="off"
              value={form.token}
              onChange={(v) => set({ token: v })}
              placeholder={registry ? '••••••••' : ''}
            />
          </Field>
        </div>
        <div className="t-caption dim">
          Il token serve per leggere e fare push. PopTown lo salva cifrato e non lo mostra più: per
          cambiarlo se ne scrive uno nuovo.
        </div>
        {test && (
          <Banner kind={test.ok ? 'info' : 'danger'}>
            {test.ok ? 'Prova riuscita: ' : 'Prova non riuscita: '}
            {test.detail}
          </Banner>
        )}
        {error && <Banner kind="danger">{error}</Banner>}
      </div>
    </Modal>
  );
}

export function Git() {
  const { toast, openModal } = useStore();
  const [data, setData] = useState<GitRegistries | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ registry: GitRegistry | null } | null>(null);

  const refresh = useCallback(
    () =>
      api.gitRegistries().then(
        (d) => {
          setData(d);
          setError(null);
        },
        (e: unknown) => setError(detail(e, 'Non riesco a leggere l’archivio dei git.')),
      ),
    [],
  );
  useEffect(() => void refresh(), [refresh]);

  const label = (kind: string) => data?.kinds.find((k) => k.id === kind)?.label ?? kind;
  const remove = (r: GitRegistry) =>
    openModal({
      kind: 'confirm',
      title: `Togliere «${r.name}»?`,
      okLabel: 'Togli',
      danger: true,
      body: r.usedBy.length
        ? `Lo usano: ${r.usedBy.join(', ')}. Quei progetti restano, senza git di riferimento.`
        : 'Il token salvato viene cancellato.',
      payload: {
        run: async () => {
          try {
            await api.removeGitRegistry(r.id, true);
            setEditing(null);
            toast({ kind: 'success', title: `${r.name}: tolto` });
            await refresh();
          } catch (e) {
            toast({ kind: 'danger', title: 'Non si toglie', body: detail(e, 'Riprova.') });
          }
        },
      },
    });

  return (
    <div className="page" style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
      <PageHeader
        title="Git"
        subtitle="I server git che i progetti possono usare. I token stanno cifrati."
        actions={
          <Button
            variant="primary"
            icon="plus"
            disabled={data !== null && !data.keyReady}
            onClick={() => setEditing({ registry: null })}
          >
            Aggiungi git
          </Button>
        }
      />
      {data && !data.keyReady && (
        <Banner kind="danger">
          Manca la chiave di cifratura (POPTOWN_SECRET_KEY) nell’ambiente di PopTown: senza, i token
          non si salvano. Va aggiunta allo stack da AI Vault.
        </Banner>
      )}
      {error && <Banner kind="danger">{error}</Banner>}
      {data === null && !error && <SkeletonRow />}
      {data?.registries.length === 0 && (
        <EmptyState
          icon="git-fork"
          pop="devops-02"
          title="Nessun git ancora"
          hint="Aggiungine uno per i progetti: i pop ci clonano i repository e ci salvano il lavoro."
        />
      )}
      {data?.registries.map((r) => (
        <RegistryCard
          key={r.id}
          r={r}
          label={label(r.kind)}
          onEdit={() => setEditing({ registry: r })}
          onTest={async () => {
            try {
              const t = await api.testGitRegistry(r.id);
              toast({ kind: t.ok ? 'success' : 'danger', title: r.name, body: t.detail });
            } catch (e) {
              toast({ kind: 'danger', title: r.name, body: detail(e, 'Prova non riuscita.') });
            }
            await refresh();
          }}
        />
      ))}
      {editing && data && (
        <RegistryModal
          registry={editing.registry}
          kinds={data.kinds}
          onClose={() => setEditing(null)}
          onSaved={() => void refresh()}
          onRemove={remove}
        />
      )}
    </div>
  );
}
